/**
 * Reusable date popover component with expandable details
 * Accepts custom trigger as children, shows date details in popover
 */

import type { ReactNode } from 'react';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { PrecisionBadge } from '@/components/ui/precision-badge';
import { ChronologyBadge } from '@/components/ui/chronology-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from './button';
import { Badge } from './badge';
import { Info, Check } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useUserSettingsContext } from '@/lib/context/user-settings-context';
import { updatePreferredChronology } from '@/lib/user-settings';
import { toast } from 'sonner';
import { resolveDisplayDate } from '@/lib/date-utils';

interface DatePopoverProps {
    /** The trigger element (button, badge, text, etc.) */
    children?: ReactNode;
    /** The primary date to display */
    date: DateProposal;
    /** Optional alternative dating proposals */
    alternativeDates?: DateProposal[];
    /** Optional title for the popover (e.g., "Passage Date", "Event Date") */
    title?: string;
}

/**
 * Popover component for displaying date details
 * Pass the trigger element as children
 */
export function DatePopover({ children, date: primaryDate, alternativeDates: initialAlternativeDates, title }: DatePopoverProps) {
    const isDarkMode = useDarkMode();
    const { settings, refresh } = useUserSettingsContext();

    const { activeDate: date, alternatives: alternativeDates } = resolveDisplayDate(
        primaryDate,
        initialAlternativeDates,
        settings?.preferredChronology
    );

    const handleSetPrimary = async (chronology: BiblicalChronology) => {
        if (!settings?.userId) return;
        try {
            await updatePreferredChronology(settings.userId, chronology);
            await refresh();
            toast.success(`Priority set to ${chronology} chronology`);
        } catch (error) {
            console.error('Error updating chronology preference:', error);
            toast.error('Failed to update preference');
        }
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                {children || (
                    <Button variant="ghost" className="p-0 h-auto">
                        <Badge variant="outline" className={`text-xs cursor-pointer ${isDarkMode ? 'text-gray-300 border-gray-600 hover:bg-gray-800' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                            {date.displayDate}
                            <Info className={`h-3.5 w-3.5 ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </Badge>
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent className="w-96 max-h-[80vh] overflow-y-auto" align="start">
                <div className="space-y-4">
                    {/* Primary Date */}
                    <div>
                        {title && (
                            <h4 className="font-semibold text-sm mb-2">{title}</h4>
                        )}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-lg font-bold">{date.displayDate}</span>
                                {date.chronology && (
                                    <ChronologyBadge chronology={date.chronology} />
                                )}
                                {date.precision && (
                                    <PrecisionBadge
                                        precision={date.precision}
                                        context="dating"
                                    />
                                )}
                                {date.confidence && (
                                    <ConfidenceBadge
                                        confidence={date.confidence}
                                        context="dating"
                                    />
                                )}
                            </div>
                            {date.rationale && (
                                <p className="text-sm">
                                    {date.rationale}
                                </p>
                            )}
                            {date.calendarDate && (
                                <div className="mt-2 p-2 bg-muted/30 rounded text-xs space-y-1">
                                    <p className="font-semibold text-muted-foreground uppercase tracking-wide">Calendar Date</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                                        {date.calendarDate.hebrewMonth && (
                                            <span>
                                                <strong>Hebrew:</strong> {date.calendarDate.hebrewMonth}
                                                {date.calendarDate.hebrewDay && ` ${date.calendarDate.hebrewDay}`}
                                            </span>
                                        )}
                                        {date.calendarDate.gregorianMonth && (
                                            <span>
                                                <strong>Gregorian:</strong> {new Date(2000, date.calendarDate.gregorianMonth - 1, date.calendarDate.gregorianDay || 1).toLocaleDateString('en-US', { month: 'long', day: date.calendarDate.gregorianDay ? 'numeric' : undefined })}
                                            </span>
                                        )}
                                        {date.calendarDate.season && (
                                            <span>
                                                <strong>Season:</strong> {date.calendarDate.season.charAt(0).toUpperCase() + date.calendarDate.season.slice(1)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {date.sources?.length > 0 && (
                                <SourceLinks sources={date.sources} />
                            )}
                        </div>
                    </div>

                    {/* Alternative Dates */}
                    {alternativeDates && alternativeDates.length > 0 && (
                        <div className="border-t pt-4">
                            <h4 className="font-semibold text-sm mb-3">Alternative Dating Proposals</h4>
                            <div className="space-y-3">
                                {alternativeDates.map((altDate, index) => (
                                    <div key={index} className="space-y-2 p-3 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold">{altDate.displayDate}</span>
                                            <ChronologyBadge chronology={altDate.chronology} />
                                            {altDate.precision && (
                                                <PrecisionBadge
                                                    precision={altDate.precision}
                                                    context="dating"
                                                />
                                            )}
                                            {altDate.confidence && (
                                                <ConfidenceBadge
                                                    confidence={altDate.confidence}
                                                    context="dating"
                                                />
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {altDate.rationale}
                                        </p>
                                        {altDate.calendarDate && (
                                            <div className="mt-2 p-2 bg-muted/30 rounded text-xs space-y-1">
                                                <p className="font-semibold text-muted-foreground uppercase tracking-wide">Calendar Date</p>
                                                <div className="flex flex-wrap gap-x-3 gap-y-1">
                                                    {altDate.calendarDate.hebrewMonth && (
                                                        <span>
                                                            <strong>Hebrew:</strong> {altDate.calendarDate.hebrewMonth}
                                                            {altDate.calendarDate.hebrewDay && ` ${altDate.calendarDate.hebrewDay}`}
                                                        </span>
                                                    )}
                                                    {altDate.calendarDate.gregorianMonth && (
                                                        <span>
                                                            <strong>Gregorian:</strong> {new Date(2000, altDate.calendarDate.gregorianMonth - 1, altDate.calendarDate.gregorianDay || 1).toLocaleDateString('en-US', { month: 'long', day: altDate.calendarDate.gregorianDay ? 'numeric' : undefined })}
                                                        </span>
                                                    )}
                                                    {altDate.calendarDate.season && (
                                                        <span>
                                                            <strong>Season:</strong> {altDate.calendarDate.season.charAt(0).toUpperCase() + altDate.calendarDate.season.slice(1)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {altDate.sources && altDate.sources.length > 0 && (
                                            <SourceLinks sources={altDate.sources} />
                                        )}
                                        <div className="pt-1">
                                            {altDate.chronology !== 'Consensus' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 text-xs gap-1.5 px-2 -ml-2 text-muted-foreground hover:text-foreground"
                                                    onClick={() => handleSetPrimary(altDate.chronology)}
                                                >
                                                    <Check className="h-3 w-3" />
                                                    Set as my primary dating preference
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </PopoverContent>
        </Popover>
    );
}

